import * as Yup from "yup";

import { OPTIONEXIBIRINSCRITO, OPTIONTIPOINSCRICAO } from "helpers/constants";
import { currencyToNumber, numbersOnly } from "helpers/utils";
import formCadastrarEvento from "./forms";

const { formField } = formCadastrarEvento;
const {
  titulo,
  limiteInscricao,
  dataEvento,
  horaLargada,
  inicioIncricao,
  horaInicioInsc,
  fimInscricao,
  horaFinalInsc,
  valorServico,
  taxaDescontoBoletoEq,
  taxaInscricao,
  qtdParcelaCartao,
  dataVencBoleto,
  dataVencPix,
  dataVencCartao,
  regulamento,
  resumo,
  termoResp,
  infoRetiradaKit,
  urlVideoDivulgacao,
  urlFacebookDivulgacao,
  urlInstagramDivulgacao,
  exibirDestaque,
  exibirMap,
  isentarMorador,
  nomeDoLocal,
  latitude,
  longitude,
  statusId,
  modalidadeId,
  tipoServicoId,
  groupInsc,
  exibirListInscrito,
  organizadorId,
  cep,
  logradouro,
  numero,
  bairro,
  cidade,
  uf,
  complemento,
} = formField;

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const HTTP_URL_PATTERN = /^https?:\/\//i;

function requiredText(label: string, max?: number) {
  let schema = Yup.string()
    .transform((value) => String(value ?? "").trim())
    .required(`${label} é obrigatório`);

  if (max) {
    schema = schema.max(max, `${label} deve ter no máximo ${max} caracteres`);
  }

  return schema;
}

function optionalText(max?: number) {
  let schema = Yup.string()
    .transform((value) => String(value ?? "").trim())
    .nullable();
  if (max) schema = schema.max(max, `Informe no máximo ${max} caracteres`);
  return schema;
}

function currencyNumber(label: string, max: number) {
  return Yup.number()
    .transform((_value, originalValue) => {
      if (
        originalValue === null ||
        originalValue === undefined ||
        originalValue === ""
      ) {
        return NaN;
      }
      if (typeof originalValue === "number") return originalValue;
      if (!/\d/.test(String(originalValue))) return NaN;
      return currencyToNumber(String(originalValue));
    })
    .typeError(`${label} possui formato monetário inválido`)
    .min(0, `${label} não pode ser negativo`)
    .max(max, `${label} deve ser menor ou igual a ${max}`)
    .test(
      "decimal-places",
      `${label} deve ter no máximo duas casas decimais`,
      (value) =>
        value === undefined ||
        Math.abs(value * 100 - Math.round(value * 100)) < 1e-8
    )
    .required(`${label} é obrigatório`);
}

function requiredInteger(label: string) {
  return Yup.number()
    .transform((_value, originalValue) => {
      if (originalValue === "" || originalValue == null) return NaN;
      return Number(originalValue);
    })
    .typeError(`${label} deve ser um número`)
    .integer(`${label} deve ser um número inteiro`)
    .moreThan(0, `${label} deve ser maior que zero`)
    .required(`${label} é obrigatório`);
}

function requiredDate(label: string) {
  return Yup.string()
    .required(`${label} é obrigatória`)
    .matches(/^\d{4}-\d{2}-\d{2}$/, `${label} possui formato inválido`)
    .test("valid-date", `${label} possui uma data inválida`, (value) => {
      if (!value) return false;
      const [year, month, day] = value.split("-").map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
      );
    });
}

function requiredTime(label: string) {
  return Yup.string()
    .required(`${label} é obrigatório`)
    .matches(TIME_PATTERN, `${label} deve estar no formato HH:mm`);
}

function optionalHttpUrl(label: string) {
  return Yup.string()
    .transform((value) => String(value ?? "").trim())
    .max(2048, `${label} deve ter no máximo 2048 caracteres`)
    .test(
      "http-url",
      `${label} deve começar com http:// ou https://`,
      (value) => !value || (HTTP_URL_PATTERN.test(value) && isValidUrl(value))
    );
}

function isValidUrl(value: string) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function dateTime(date: unknown, time: unknown) {
  if (!date || !time) return "";
  return `${date}T${time}`;
}

function isEnabled(value: unknown) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function requiredBooleanFlag(label: string) {
  return Yup.mixed()
    .required(`${label} é obrigatório`)
    .test(
      "boolean-flag",
      `${label} possui valor inválido`,
      (value) =>
        value === true ||
        value === false ||
        value === 1 ||
        value === 0 ||
        value === "1" ||
        value === "0" ||
        value === "true" ||
        value === "false"
    );
}

export function getSchemaCadastroEvento(isEdit = false) {
  return Yup.object().shape({
    [titulo.name]: requiredText("Título"),
    [dataEvento.name]: requiredDate("Data do evento").test(
      "after-registration",
      "O evento deve começar após o encerramento das inscrições",
      function (value) {
        const eventStart = dateTime(value, this.parent.horaLargada);
        const registrationEnd = dateTime(
          this.parent.fimInscricao,
          this.parent.horaFinalInsc
        );
        return !eventStart || !registrationEnd || eventStart >= registrationEnd;
      }
    ),
    [horaLargada.name]: requiredTime("Hora da largada"),
    [inicioIncricao.name]: requiredDate("Data de início das inscrições"),
    [horaInicioInsc.name]: requiredTime("Hora de início das inscrições"),
    [fimInscricao.name]: requiredDate("Data de fim das inscrições").test(
      "after-registration-start",
      "O fim das inscrições deve ocorrer após o início",
      function (value) {
        const registrationStart = dateTime(
          this.parent.inicioIncricao,
          this.parent.horaInicioInsc
        );
        const registrationEnd = dateTime(value, this.parent.horaFinalInsc);
        return (
          !registrationStart ||
          !registrationEnd ||
          registrationEnd >= registrationStart
        );
      }
    ),
    [horaFinalInsc.name]: requiredTime("Hora de fim das inscrições"),
    [organizadorId.name]: requiredInteger("Organizador"),
    [modalidadeId.name]: requiredInteger("Modalidade"),
    [tipoServicoId.name]: requiredInteger("Tipo de serviço"),
    [valorServico.name]: currencyNumber(
      "Valor do serviço",
      9_999_999_999_999.99
    ),
    [limiteInscricao.name]: requiredInteger("Limite de inscrições"),
    [groupInsc.name]: Yup.string()
      .required("Tipo de inscrição é obrigatório")
      .oneOf(
        OPTIONTIPOINSCRICAO.map((item) => item.value),
        "Selecione um tipo de inscrição válido"
      ),
    [exibirListInscrito.name]: Yup.string()
      .required("Exibição da lista de inscritos é obrigatória")
      .oneOf(
        OPTIONEXIBIRINSCRITO.map((item) => item.value),
        "Selecione uma opção válida para a lista de inscritos"
      ),
    [statusId.name]: isEdit
      ? requiredInteger("Status do evento")
      : Yup.string().nullable(),
    [taxaInscricao.name]: currencyNumber("Taxa de inscrição", 9_999.99),
    [qtdParcelaCartao.name]: requiredInteger("Quantidade de parcelas"),
    [taxaDescontoBoletoEq.name]: Yup.number()
      .transform((_value, originalValue) => {
        if (originalValue === "" || originalValue == null) return NaN;
        return Number(String(originalValue).replace(",", "."));
      })
      .typeError("Percentual de desconto deve ser um número")
      .min(0, "Percentual de desconto não pode ser negativo")
      .max(100, "Percentual de desconto deve ser no máximo 100")
      .test(
        "decimal-places",
        "Percentual de desconto deve ter no máximo duas casas decimais",
        (value) =>
          value === undefined ||
          Math.abs(value * 100 - Math.round(value * 100)) < 1e-8
      )
      .required("Percentual de desconto é obrigatório"),
    [dataVencPix.name]: requiredDate("Data de vencimento do Pix"),
    [dataVencBoleto.name]: requiredDate("Data de vencimento do boleto"),
    [dataVencCartao.name]: requiredDate("Data de vencimento do cartão"),
    [urlVideoDivulgacao.name]: optionalHttpUrl("Link do YouTube"),
    [urlFacebookDivulgacao.name]: optionalHttpUrl("Link do Facebook"),
    [urlInstagramDivulgacao.name]: optionalHttpUrl("Link do Instagram"),
    [exibirDestaque.name]: requiredBooleanFlag("Exibição em destaque"),
    [exibirMap.name]: requiredBooleanFlag("Exibição do mapa"),
    [isentarMorador.name]: requiredBooleanFlag("Isenção para moradores"),
    [regulamento.name]: optionalText(),
    [termoResp.name]: optionalText(),
    [resumo.name]: optionalText(),
    [infoRetiradaKit.name]: optionalText(),
    [cep.name]: Yup.string()
      .required("CEP é obrigatório")
      .test(
        "cep",
        "CEP deve conter exatamente 8 números",
        (value) => numbersOnly(value).length === 8
      ),
    [logradouro.name]: requiredText("Logradouro", 100),
    [numero.name]: requiredText("Número", 6),
    [bairro.name]: requiredText("Bairro", 60),
    [cidade.name]: requiredText("Cidade", 60),
    [uf.name]: Yup.string()
      .transform((value) =>
        String(value ?? "")
          .trim()
          .toUpperCase()
      )
      .required("UF é obrigatória")
      .matches(/^[A-Z]{2}$/, "UF deve conter exatamente duas letras"),
    [complemento.name]: optionalText(60),
    [nomeDoLocal.name]: requiredText("Nome do local", 60),
    [latitude.name]: Yup.string()
      .transform((value) => String(value ?? "").trim())
      .test("latitude", "Latitude deve estar entre -90 e 90", function (value) {
        if (!value) return !isEnabled(this.parent.exibirMap);
        const number = Number(value);
        return Number.isFinite(number) && number >= -90 && number <= 90;
      }),
    [longitude.name]: Yup.string()
      .transform((value) => String(value ?? "").trim())
      .test(
        "longitude",
        "Longitude deve estar entre -180 e 180",
        function (value) {
          if (!value) return !isEnabled(this.parent.exibirMap);
          const number = Number(value);
          return Number.isFinite(number) && number >= -180 && number <= 180;
        }
      ),
  });
}

export const schemaCadastroEvento = getSchemaCadastroEvento(false);
